import { ButtonProps, Popover } from "antd";
import React, { useRef, useState } from "react";
import styled from "styled-components";

import { COLORS } from "../constants";
import { useOutsideContainersClick } from "../utils/dom";

import { ScrollShadowWrapper } from "./ScrollShadowWrapper";

import { Button, Checkbox, DownArrow, FlexColumn, FlexRow, InputSearch, Typography } from "./";

const StyledButton = styled(Button)<{ $active?: boolean }>`
  ${({ $active }) =>
    $active
      ? `
      &&&&.ant-btn {
        background-color: hsl(222, 87%, 97%);
        color: ${COLORS.coeblue4};

        &:active,
        &:focus,
        &:hover {
          background-color: hsl(222, 87%, 95%);
        }
      `
      : ""}
`;

const Container = styled.div`
  .ant-popover-inner-content {
    padding: 0px;
  }
`;

const StyledCheckbox = styled(Checkbox)`
  &&.ant-checkbox-wrapper {
    margin: 0px;
  }
`;

type Props = {
  hasSearch?: boolean;
  getLabel: () => React.ReactNode;
  initialValues?: string[];
  options: { label: string; value: string }[];
  onChange: (values: string[]) => void;
  buttonProps?: ButtonProps;
};

const areSetsEqual = (a: Set<string>, b: Set<string>) => {
  return a.size === b.size && [...a].every(value => b.has(value));
};

export const CheckboxListPopover: React.FC<Props> = ({
  hasSearch,
  getLabel,
  initialValues,
  options,
  onChange,
  buttonProps,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  const [search, setSearch] = useState("");

  const [values, setValues] = useState(initialValues ?? []);

  const [isOpen, setIsOpen] = useState(false);

  useOutsideContainersClick(() => setIsOpen(false), ref);

  const searchedOptions = options.filter(option =>
    option.label.toLowerCase().includes(search.toLowerCase())
  );

  const areAllSearchOptionsSelected = areSetsEqual(
    new Set(searchedOptions.map(option => option.value)),
    new Set(values)
  );

  return (
    <Container ref={ref}>
      <Popover
        trigger="click"
        placement="bottomRight"
        open={isOpen}
        getPopupContainer={node => node}
        autoAdjustOverflow={false}
        content={
          !options.length ? (
            <div style={{ padding: 8, minWidth: 200 }}>
              <Typography color="textSecondary">No options available</Typography>
            </div>
          ) : (
            <FlexColumn style={{ padding: 12, minWidth: 200 }} gap={1.5}>
              {hasSearch && (
                <InputSearch
                  autoFocus
                  placeholder="Search"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  style={{ width: "100%", padding: 4 }}
                />
              )}

              <ScrollShadowWrapper>
                <FlexColumn
                  gap={0.25}
                  style={{
                    maxWidth: 300,
                    maxHeight: 300,
                  }}
                >
                  <StyledCheckbox
                    key="select-all"
                    checked={areAllSearchOptionsSelected}
                    onChange={
                      areAllSearchOptionsSelected
                        ? () => setValues([])
                        : () => setValues(searchedOptions.map(option => option.value))
                    }
                  >
                    <span style={{ fontWeight: 600 }}>Select All</span>
                  </StyledCheckbox>

                  {searchedOptions.map(option => (
                    <StyledCheckbox
                      key={option.value}
                      checked={values.includes(option.value)}
                      onChange={() =>
                        values.includes(option.value)
                          ? setValues(values.filter(val => val !== option.value))
                          : setValues([...values, option.value])
                      }
                    >
                      {option.label}
                    </StyledCheckbox>
                  ))}
                </FlexColumn>
              </ScrollShadowWrapper>

              <FlexRow style={{ alignSelf: "flex-end" }}>
                <Button
                  type="primary"
                  onClick={() => {
                    onChange(values);
                    setIsOpen(false);
                  }}
                >
                  Apply
                </Button>
              </FlexRow>
            </FlexColumn>
          )
        }
      >
        <StyledButton
          type="default"
          $active={Boolean(initialValues?.length)}
          onClick={() => setIsOpen(!isOpen)}
          {...buttonProps}
        >
          <FlexRow>
            <div>{getLabel()}</div>
            <DownArrow />
          </FlexRow>
        </StyledButton>
      </Popover>
    </Container>
  );
};
